import { useContext, useState } from "react";
import type { AxiosError } from "axios";
import Axios from "axios";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import {
  CheckBoxContainer,
  CheckBoxFinePrintLabel,
} from "../../../../../components/Form/Form";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { endpoints } from "../../../../../endpoints";
import { Form } from "../../../../../layout/FormLayout";
import {
  isAxiosError,
  useFormWrapper,
  useStoreState,
} from "../../../../../util/util";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import type { AssetsExternalLinks } from "../../../../../types/types.PIM";
import type { DataMutate } from "../../../../../types/types";
import { useParams } from "react-router-dom";
import { CheckBoxNoLabel } from "../../../../../components/CheckBoxes/CheckBoxes";
import type { AssetContainerType } from "../../../../../util/assetContainer.types";
import { getExternalLinkAssociationKey } from "../../../../../util/assetEndpoints";

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  margin-top: 30px;
`;

export const AddandEditLink = ({
  formType,
  editData,
  refreshAssetsList,
  onSuccess,
  onCancel,
  assetContainerType = "product",
  assetContainerId,
}: {
  formType: string;
  editData?: AssetsExternalLinks;
  onSuccess: () => void;
  refreshAssetsList: DataMutate<any>;
  onCancel: () => void;
  assetContainerType?: AssetContainerType;
  assetContainerId?: string;
}) => {
  const { t } = useTranslation();

  const isAddForm = formType === "add";

  const defaultURL = editData?.url;
  const defaultName = editData?.name;
  const defaultIsVisible = editData?.is_visible;
  const linkId = editData?.id;

  const methodsOfUseForm = useFormWrapper();
  const { tenant_id } = useStoreState();

  const { product_id } = useParams<{ product_id: string }>();
  const resolvedAssetContainerId = assetContainerId ?? product_id;
  const associationKey = getExternalLinkAssociationKey(assetContainerType);

  const [submitting, setSubmitting] = useState(false);
  const [isVisible, setIsVisible] =
    useState<boolean | undefined>(defaultIsVisible);
  const { notifySuccess, notifyError } = useContext(Notifications);

  const { handleSubmit, errors, formState, register } = methodsOfUseForm;

  const onSubmit = async (values: any) => {
    if (!resolvedAssetContainerId) {
      notifyError(t("Could not edit link. Something went wrong."));
      return;
    }

    const formData = {
      ...values,
      is_visible: isVisible ?? false,
      [associationKey]: [resolvedAssetContainerId],
    };

    setSubmitting(true);

    const postBaseUrl =
      endpoints.v2_storefronts_id_pim_assets_external_links(tenant_id);

    const patchBaseUrl =
      endpoints.v2_storefronts_id_pim_assets_external_links_id(
        tenant_id,
        linkId
      );

    try {
      if (linkId) {
        await Axios.patch(patchBaseUrl, formData);
      } else {
        await Axios.post(postBaseUrl, formData);
      }
      await refreshAssetsList();
      if (isAddForm) {
        notifySuccess(t("External Link successfully added"));
      } else {
        notifySuccess(t("External Link successfully edited"));
      }
      onSuccess();
    } catch (error) {
      if (
        isAxiosError(error) &&
        (error as AxiosError)?.response?.data?.message
      ) {
        notifyError(error?.response?.data?.message);
      } else {
        notifyError(t("Could not edit link. Something went wrong."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          name={"url"}
          label={"URL"}
          theref={register({ required: true })}
          errors={errors}
          formState={formState}
          type="url"
          defaultValue={defaultURL}
        />
        <TextField
          name={"name"}
          label={"Link Name"}
          theref={register({ required: true })}
          errors={errors}
          formState={formState}
          type="text"
          defaultValue={defaultName}
        />
        {!isAddForm && (
          <CheckBoxContainer>
            <CheckBoxNoLabel
              name="is_visible"
              id="is_visible"
              defaultChecked={isVisible}
              ref={register({ required: false })}
              onChange={() => setIsVisible(!isVisible)}
            />
            <CheckBoxFinePrintLabel htmlFor="is_visible">
              {t("Link is accessible by external users")}
            </CheckBoxFinePrintLabel>
          </CheckBoxContainer>
        )}
        <ButtonContainer>
          <TertiaryButtonLargeV2
            fullWidth
            disabled={submitting}
            type="reset"
            onClick={onCancel}
          >
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 fullWidth loading={submitting} type="submit">
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </>
  );
};
